import React, { useRef, useEffect, useState } from "react";
import styled from "styled-components";
import useFileUpload from "react-use-file-upload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormattedMessage } from "react-intl";
import useGetDir from "hooks/useGetDir";
import { StyledLabel, StyledRequired } from "./styles";
import ModalComponent from "./modal";
import { FormRow } from "../styles/forms/main";
import { ConfirmModalContent } from "../styles/elements/main";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    flex: 1;
`;

const InputContainer = styled.div`
    padding: 14px;
    border: ${(props) => (props.errorMessage ? "2px dashed #bd271e" : "1px dashed #e2e3e4")};
    display: flex;
    justify-content: center;
    cursor: ${(props) => (props.notAllowed ? "not-allowed" : "pointer")};
    margin-top: 4px;
    input {
        :disabled {
            background-color: rgba(211, 218, 230, 0.05);
        }
    }
`;

const Content = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    font-weight: 400;
    font-size: 14px;
    color: #98a2b3;
`;

const Label = styled.label`
    font-weight: 400;
    font-size: 14px;
    color: #69707d;
`;

const FilesHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 7px;
`;

const DeleteButton = styled.button`
    padding: 7px 8px;
    background-color: #ffecec;
    display: flex;
    align-items: center;
    color: #bd271e;
    border-radius: 4px;
    gap: 10px;

    svg {
        color: #bd271e;
    }
`;

const FilesContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 15px;
    column-gap: 15px;
    width: 100%;
    height: ${(props) => (props.length > 2 ? "230px" : "auto")};
    overflow: ${(props) => (props.length > 2 ? "scroll" : "")};
`;

const File = styled.div`
    padding: 11px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 500;
    font-size: 14px;
    color: #000000;
    border: 1px solid #e2e3e4;
    border-radius: 6px;
`;

const IconsContainer = styled.div`
    display: flex;
    gap: 5px;
`;

const DeleteAllButton = styled.button`
    background: #ffecec;
    border: none;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    color: #bd271e;
    cursor: pointer;
`;

const uploadIconStyle = {
    color: "#006bb4",
};

const downloadIconStyle = {
    color: "#006bb4",
    cursor: "pointer",
};

const cancelIconStyle = {
    color: "#BD271E",
    cursor: "pointer",
};
const ErrorMessage = styled.p`
    color: #bd271e;
    font-weight: 400;
    font-size: 12px;
    margin-top: 4px;
`;
const ErrorLabel = styled(StyledLabel)`
    color: #bd271e;
`;
function UploadAttachments({
    label,
    onFilesChange,
    uploadedAttachments,
    isEditable,
    onDeleteUploadedItem,
    onDeleteAllAttachements,
    required,
    name,
    errorMessage,
    maxLength,
    showConfirmation,
    hideDeleteAllButton,
}) {
    const { files, fileNames, handleDragDropEvent, clearAllFiles, setFiles, removeFile } =
        useFileUpload();
    const [isFirstRender, setIsFirstRender] = useState(true);

    const inputRef = useRef();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmationId, setConfirmationId] = useState(null);
    const dir = useGetDir();

    useEffect(() => {
        if (isEditable) {
            if (!isFirstRender) {
                onFilesChange(files, name);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);
    useEffect(() => {
        if (isFirstRender) {
            setIsFirstRender(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const downloadAttachement = (file) => {
        window.open(file.downloadLink, "_blank");
    };

    const previewAttachement = (file) => {
        window.open(file.previewLink, "_blank");
    };
    const handleDeleteUploadedItem = (id, documentType) => {
        if (showConfirmation) {
            setConfirmationId(id);
            setShowConfirmationModal(true);
        } else {
            onDeleteUploadedItem(id, documentType);
        }
    };

    return (
        <Container>
            {/* Provide a drop zone and an alternative button inside it to upload files. */}
            {isEditable && (
                <div>
                    {errorMessage ? (
                        <ErrorLabel>
                            {label} {required && <StyledRequired>*</StyledRequired>}
                        </ErrorLabel>
                    ) : (
                        <StyledLabel>
                            {label} {required && <StyledRequired>*</StyledRequired>}
                        </StyledLabel>
                    )}
                    <InputContainer
                        errorMessage={errorMessage}
                        onDragEnter={handleDragDropEvent}
                        onDragOver={handleDragDropEvent}
                        maxLength={maxLength}
                        showConfirmation={showConfirmation}
                        hideDeleteAllButton={hideDeleteAllButton}
                        notAllowed={files.length + (uploadedAttachments?.length ?? 0) === maxLength}
                        onDrop={(e) => {
                            handleDragDropEvent(e);
                            setFiles(e, "a");
                        }}
                        onClick={() => inputRef.current.click()}
                    >
                        <Content>
                            <FormattedMessage id="upload.files" /> PDF, Word, PNG, or JPEG{" "}
                            <FileUploadIcon style={uploadIconStyle} />
                        </Content>
                        {/* Hide the default HTML input */}
                        <input
                            ref={inputRef}
                            type="file"
                            multiple
                            style={{ display: "none" }}
                            onChange={(e) => {
                                setFiles(e, "a");
                                inputRef.current.value = null;
                            }}
                            name={name}
                            disabled={
                                files.length + (uploadedAttachments?.length ?? 0) === maxLength
                            }
                        />
                    </InputContainer>
                </div>
            )}

            {/* Display the files to be uploaded */}
            {files.length > 0 && (
                <div>
                    <FilesHeader>
                        <Label>
                            <FormattedMessage id="label.new-uploaded-documents" />{" "}
                        </Label>
                        {!hideDeleteAllButton && (
                            <DeleteAllButton
                                type="button"
                                onClick={clearAllFiles}
                            >
                                <CancelIcon style={cancelIconStyle} />
                                <FormattedMessage id="action.delete-all" />
                            </DeleteAllButton>
                        )}
                    </FilesHeader>
                    <FilesContainer>
                        {fileNames.map((name, index) => (
                            <File key={name}>
                                {name}
                                <DeleteButton
                                    type="button"
                                    onClick={() => removeFile(index)}
                                >
                                    <DeleteIcon />
                                </DeleteButton>
                            </File>
                        ))}
                    </FilesContainer>
                </div>
            )}

            {uploadedAttachments?.length > 0 && (
                <>
                    <FilesHeader>
                        <Label>
                            {dir === "rtl" ? (
                                <>
                                    {label} <FormattedMessage id="label.uploaded" />
                                </>
                            ) : (
                                <>
                                    <FormattedMessage id="label.uploaded" /> {label}
                                </>
                            )}
                        </Label>

                        {isEditable &&
                            (uploadedAttachments?.length === 1 || hideDeleteAllButton ? null : (
                                <DeleteAllButton
                                    type="button"
                                    onClick={onDeleteAllAttachements}
                                >
                                    <CancelIcon style={cancelIconStyle} />
                                    <FormattedMessage id="action.delete-all" />
                                </DeleteAllButton>
                            ))}
                    </FilesHeader>
                    <FormRow>
                        <FilesContainer length={uploadedAttachments?.length}>
                            {uploadedAttachments.map((item) => (
                                <File key={item.id}>
                                    {item.documentName}
                                    <IconsContainer>
                                        <RemoveRedEyeIcon
                                            onClick={() => previewAttachement(item)}
                                            style={downloadIconStyle}
                                        />
                                        <FileDownloadIcon
                                            onClick={() => downloadAttachement(item)}
                                            style={downloadIconStyle}
                                        />
                                        {isEditable && (
                                            <CancelIcon
                                                style={cancelIconStyle}
                                                onClick={() =>
                                                    handleDeleteUploadedItem(
                                                        item.id,
                                                        item.documentType
                                                    )
                                                }
                                                // onDeleteUploadedItem(item.id, item.documentType)
                                            />
                                        )}

                                        {showConfirmationModal && item?.id === confirmationId && (
                                            <ModalComponent
                                                header="Alert"
                                                open={showConfirmationModal}
                                                onClose={() => {
                                                    setShowConfirmationModal(false);
                                                }}
                                                onSubmit={() => {
                                                    onDeleteUploadedItem(
                                                        item.id,
                                                        item.documentType
                                                    );
                                                }}
                                                submitBtnText={
                                                    <FormattedMessage id="action.save" />
                                                }
                                                submitBtn
                                                cancelBtn
                                                isFooter
                                            >
                                                <ConfirmModalContent>
                                                    <FormattedMessage id="upload.files.detele" />
                                                </ConfirmModalContent>
                                            </ModalComponent>
                                        )}
                                    </IconsContainer>
                                </File>
                            ))}
                        </FilesContainer>
                    </FormRow>
                </>
            )}
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </Container>
    );
}

export default UploadAttachments;
